import { useState } from "react";
import { ClickableIcon } from "../../../../../../components/Icon/ClickableIcon.tsx";
import { Query } from "../../../../../../components/Query/Query.tsx";
import { PanelSection } from "../../../../components/Panel/PanelSection.tsx";
import { PanelSubsection } from "../../../../components/Panel/PanelSubsection.tsx";
import { useSelectedAsset } from "../../../../hooks/useSelectedAsset.ts";
import type {
  Asset,
  ImageGenerationInformation,
  NodeGenerationParameters,
} from "../../../../types.ts";
import { TextItemSectionDetails } from "../../../components/SectionDetails.tsx";

export const DebugSection = () => {
  const { selectedAsset: asset } = useSelectedAsset();

  if (!asset) return null;

  return <DebugSectionContent asset={asset} />;
};

const DebugSectionContent = ({ asset }: { asset: Asset }) => {
  const image = asset.content;
  const deletedAt = asset.deleted_at;

  return (
    <PanelSection title="Debug">
      <>
        {deletedAt !== null && (
          <TextItemSectionDetails
            name="Deleted at"
            value={new Date(deletedAt).toLocaleString()}
          />
        )}
        <Query
          queryKey={`contents/${image.uuid}/generation-parameters`}
          enabled={!!image}
        >
          {(imageGenerationParameters: ImageGenerationInformation) =>
            imageGenerationParameters.node_generation_parameters.map(
              (it, index) => <NodeParametersSubsection key={index} node={it} />,
            )
          }
        </Query>
      </>
    </PanelSection>
  );
};

const NodeParametersSubsection = ({
  node,
}: {
  node: NodeGenerationParameters;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <PanelSubsection
      title={node.name}
      isOpen={isOpen}
      rightItem={
        <ClickableIcon
          name={isOpen ? "ChevronUp" : "ChevronDown"}
          size="sm"
          variant="tertiary"
          onClick={() => setIsOpen(!isOpen)}
        />
      }
    >
      <TextItemSectionDetails name="Status" value={node.status} />
      {node.started_at && (
        <TextItemSectionDetails
          name="Started at"
          value={new Date(node.started_at).toLocaleString()}
        />
      )}
      {node.ended_at && (
        <TextItemSectionDetails
          name="Ended at"
          value={new Date(node.ended_at).toLocaleString()}
        />
      )}
      <div className="w-full flex-col gap-050">
        <span className="label-md-default text-secondary">Parameters</span>
        {Object.keys(node.parameters).map((key) => (
          <div key={key} className="grid grid-cols-2 grid-flow-row">
            <div className="w-[80px] truncate label-sm-semibold text-primary">
              {key}{" "}
            </div>
            <div className="flex-fill break-words label-sm-default text-secondary">
              {JSON.stringify(
                node.parameters[key as keyof typeof node.parameters],
              )}
            </div>
          </div>
        ))}
      </div>
    </PanelSubsection>
  );
};
